import { isCapacitorNativeSync, triggerNativeBack } from './bridge';

/**
 * iOS-Edge-Swipe-Back-Geste.
 *
 * Problem: WKWebView reicht die native Edge-Swipe-Back-Geste NICHT an JS
 * weiter, wenn `allowsBackForwardNavigationGestures` aus ist. Wir erkennen
 * die Geste daher in JS und loesen die zentrale Back-Logik aus
 * (`triggerNativeBack` -> `handleNativeBack`), damit Modals/Sheets
 * (Back-Closer) und Stream-Guards genauso respektiert werden wie beim
 * Hardware-Back-Button auf Android.
 *
 * Reagiert nur am linken Bildschirmrand und nur bei einer sauberen
 * horizontalen Rechts-Wisch-Geste; horizontale Scroll-Container
 * (z.B. world-tree) bleiben ungestoert.
 */

interface CapacitorWithPlatform {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

interface GestureState {
  startX: number;
  startY: number;
  startTime: number;
  pointerId: number;
}

// Mindest-Distanz nach rechts, ab der die Geste als Back gilt.
const MIN_DX_PX = 60;
// dx muss groesser als das DY_RATIO-fache der Vertikalbewegung sein,
// damit reine Scrolls/Vertikal-Drags nicht versehentlich ausloesen.
const HORIZONTAL_DOMINANCE = 2;
// Maximale Geste-Dauer; langsamere Wisches zaehlen nicht als Back.
const MAX_GESTURE_DURATION_MS = 1500;
// Fallback-Mindestbreite der Edge-Zone in CSS-Pixeln.
const MIN_EDGE_ZONE_PX = 24;

function isIosNative(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isCapacitorNativeSync()) return false;
  const cap = (window as Window & { Capacitor?: CapacitorWithPlatform }).Capacitor;
  return cap?.getPlatform?.() === 'ios';
}

/**
 * Liest den linken Safe-Area-Inset (definiert in styles/base.css als
 * `--lorath-safe-left`) aus dem Document-Root. Fallback: 0.
 */
function readSafeLeftPx(): number {
  if (typeof window === 'undefined' || !window.document?.documentElement) return 0;
  const raw = window.getComputedStyle(window.document.documentElement)
    .getPropertyValue('--lorath-safe-left')
    .trim();
  if (!raw) return 0;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function computeEdgeZonePx(): number {
  return Math.max(MIN_EDGE_ZONE_PX, readSafeLeftPx());
}

/**
 * Installiert die Touch-Listener fuer die Edge-Swipe-Back-Geste.
 * Nur aktiv, wenn `isIosNative()` true liefert. Auf Android (Hardware-Back
 * vorhanden) und im Web bleibt der Listener inaktiv.
 *
 * @returns Cleanup-Funktion, die Listener wieder entfernt (App-Unmount).
 */
export function installEdgeSwipeBack(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const doc = window.document;
  if (!doc?.addEventListener) return () => undefined;
  if (!isIosNative()) return () => undefined;

  let state: GestureState | null = null;

  const handleTouchStart = (event: TouchEvent): void => {
    if (state) return; // laufende Geste hat Vorrang
    if (event.touches.length !== 1) return; // Multi-Touch ignorieren
    const touch = event.touches[0];
    if (!touch) return;
    if (touch.clientX > computeEdgeZonePx()) return; // ausserhalb der Rand-Zone
    state = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      pointerId: touch.identifier,
    };
  };

  const handleTouchMove = (event: TouchEvent): void => {
    if (!state) return;
    // Multi-Touch bricht die Geste ab.
    if (event.touches.length !== 1) {
      state = null;
      return;
    }
    const touch = event.touches[0];
    if (!touch || touch.identifier !== state.pointerId) {
      state = null;
      return;
    }
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;
    // Links-Bewegung verwirft die Geste frueh, damit Folgeframes das
    // Default-Scrolling nicht stoeren.
    if (dx <= 0) {
      state = null;
      return;
    }
    if (dx < MIN_DX_PX) return;
    if (dx <= HORIZONTAL_DOMINANCE * Math.abs(dy)) return;
    if (Date.now() - state.startTime > MAX_GESTURE_DURATION_MS) {
      state = null;
      return;
    }
    // Erst jetzt ist klar, dass es eine Back-Geste ist: Default-Verhalten
    // (Scroll/Pull-to-refresh etc.) fuer die restlichen Frames unterbinden.
    try {
      event.preventDefault();
    } catch {
      // passive listener oder sandboxed: Back trotzdem ausloesen.
    }
    // State vor triggerNativeBack zuruecksetzen, damit Folgeevents sauber sind
    // (Closers koennen synchron wieder touch-Handler anstossen).
    state = null;
    triggerNativeBack();
  };

  const reset = (): void => {
    state = null;
  };

  // touchstart: passiv, weil wir nie preventDefault brauchen.
  doc.addEventListener('touchstart', handleTouchStart, { passive: true });
  // touchmove: non-passiv, weil wir bei klarer Back-Geste preventDefault rufen.
  doc.addEventListener('touchmove', handleTouchMove, { passive: false });
  doc.addEventListener('touchend', reset, { passive: true });
  doc.addEventListener('touchcancel', reset, { passive: true });

  return () => {
    doc.removeEventListener('touchstart', handleTouchStart);
    doc.removeEventListener('touchmove', handleTouchMove);
    doc.removeEventListener('touchend', reset);
    doc.removeEventListener('touchcancel', reset);
    state = null;
  };
}
